import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  LinearProgress,
  Divider,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Psychology as PsychologyIcon,
  EventNote as EventNoteIcon,
  TrackChanges as TrackChangesIcon,
  Assignment as AssignmentIcon,
  Chat as ChatIcon,
  MenuBook as MenuBookIcon,
  Timeline as TimelineIcon,
  Settings as SettingsIcon,
  SupervisorAccount as SupervisorAccountIcon,
  NotificationsActive as NotificationsActiveIcon
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';
import { formatDistanceToNow } from 'date-fns';
import api, { getCheckInChartData } from '../../services/api';
import * as clinicalSupervisorApi from '../../services/clinicalSupervisorApi';
import therapistApi from '../../services/therapistApi';
import * as supervisorApi from '../../services/supervisorApi';
import therapistStyles from './styles/therapistStyles';
import { tokens, statusColor } from './styles/tokens';
import { dashboardTheme } from '../v2/styles/dashboardTheme';
import OnboardingStatusCard from './dashboard/OnboardingStatusCard';
import UpcomingAppointmentsWidget from '../call/UpcomingAppointmentsWidget';

/**
 * ClientDashboardView Component
 * Provides a dashboard snapshot view of a client for therapists and supervisors
 */
const ClientDashboardView = ({ clientId, clientInfo, token, onNavigateToTab, apiContext = 'therapist' }) => {
  // Helper functions to call appropriate APIs based on context
  const getClientGoalsApi = (clientId, token) => {
    if (apiContext === 'supervisor') {
      return clinicalSupervisorApi.getClientGoals(clientId, token);
    }
    return api.getTherapistClientGoals(clientId, token);
  };

  const getClientJournalApi = (clientId, token) => {
    if (apiContext === 'supervisor') {
      return clinicalSupervisorApi.getClientJournal(clientId, token);
    }
    return api.getTherapistClientJournal(clientId, token);
  };

  const getClientProgressApi = (clientId, token) => {
    if (apiContext === 'supervisor') {
      return clinicalSupervisorApi.getClientProgress(clientId, token);
    }
    return api.getTherapistClientProgress(clientId, token);
  };

  const getClientConversationsApi = (clientId, token) => {
    if (apiContext === 'supervisor') {
      return clinicalSupervisorApi.getClientConversations(clientId, token);
    }
    return api.getClientConversations(clientId, token);
  };

  const getClientCheckInsApi = (clientId, token) => {
    if (apiContext === 'supervisor') {
      return clinicalSupervisorApi.getClientCheckIns(clientId, token);
    }
    return api.getClientCheckIns(clientId, token);
  };

  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    checkIns: [],
    goals: [],
    journalEntries: [],
    progressData: null,
    recentActivity: [],
    stats: {},
    latestConversation: null,
    sessionSummaryData: null,
    careNotesCount: 0,
    consultationsCount: 0,
    onboarding: null,
    triage: null
  });

  // AI Supervisor menu state
  const [aiSupervisorMenuAnchor, setAiSupervisorMenuAnchor] = useState(null);
  const [conversationDialogOpen, setConversationDialogOpen] = useState(false);
  const [availableConversations, setAvailableConversations] = useState([]);

  // Template chart data from API
  const [templateChartData, setTemplateChartData] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    if (clientId && token) {
      fetchDashboardData();
    }
  }, [clientId, token]);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all data in parallel using context-aware API calls
      const [checkInsRes, goalsRes, journalRes, progressRes, conversationsRes, careNotesRes, consultationsRes, onboardingRes, triageRes] = await Promise.all([
        getClientCheckInsApi(clientId, token),
        getClientGoalsApi(clientId, token),
        getClientJournalApi(clientId, token),
        getClientProgressApi(clientId, token),
        getClientConversationsApi(clientId, token),
        therapistApi.getClientCareNotes(clientId, false, token).catch(() => ({ careNotes: [] })),
        supervisorApi.getConsultationHistory({ clientId }, token).catch(() => ({ consultations: [] })),
        api.getClientOnboardingDetails(clientId, token).catch(() => ({ status: 'error' })),
        api.getClientTriageDetails(clientId, token).catch(() => ({ status: 'error' }))
      ]);

      // Process check-ins
      const checkIns = checkInsRes?.checkIns || [];
      
      // Process goals
      const goals = goalsRes?.goals || goalsRes || [];
      const activeGoals = goals.filter(g => g.status === 'active');
      const completedGoals = goals.filter(g => g.status === 'completed');

      // Process journal entries
      const journalEntries = journalRes?.entries || journalRes || [];

      // Combine recent activity
      const recentActivity = combineRecentActivity(checkIns, goals, journalEntries);

      // Calculate stats - support both legacy and template-based check-ins
      const legacyCheckIns = checkIns.filter(c => c.moodRating != null);
      const templateCheckIns = checkIns.filter(c => c.dynamicResponses?.length > 0);

      // Calculate average based on what type of check-ins exist
      let averageValue = 0;
      let averageLabel = 'Mood';
      if (legacyCheckIns.length > 0) {
        averageValue = (legacyCheckIns.reduce((sum, c) => sum + (c.moodRating || 0), 0) / legacyCheckIns.length).toFixed(1);
      } else if (templateCheckIns.length > 0) {
        // For template check-ins, show SUDS average or presence average
        const sudsValues = templateCheckIns.map(c => c.sudsRating).filter(v => v != null);
        const presenceValues = templateCheckIns.map(c => c.presenceRating).filter(v => v != null);
        if (sudsValues.length > 0) {
          averageValue = (sudsValues.reduce((s, v) => s + v, 0) / sudsValues.length).toFixed(1);
          averageLabel = 'SUDS';
        } else if (presenceValues.length > 0) {
          averageValue = (presenceValues.reduce((s, v) => s + v, 0) / presenceValues.length).toFixed(1);
          averageLabel = 'Presence';
        }
      }

      const stats = {
        totalCheckIns: checkIns.length,
        averageMood: averageValue,
        averageLabel,
        hasTemplateCheckIns: templateCheckIns.length > 0,
        activeGoals: activeGoals.length,
        completedGoals: completedGoals.length,
        totalJournalEntries: journalEntries.length,
        lastCheckIn: checkIns[0] ? formatDistanceToNow(new Date(checkIns[0].date || checkIns[0].createdAt), { addSuffix: true }) : 'Never'
      };

      // Get latest conversation with messages (excluding deleted conversations)
      const conversations = conversationsRes?.conversations || [];
      
      // Debug logging to understand what we're working with
      console.log('[Dashboard] All conversations:', conversations.map(c => ({
        id: c._id,
        messageCount: c.messages?.length,
        isDeleted: c.isDeleted,
        createdAt: c.createdAt,
        updatedAt: c.updatedAt
      })));
      
      // Filter conversations more carefully
      const filteredConversations = conversations.filter(c => {
        // Exclude if explicitly deleted
        if (c.isDeleted === true) return false;
        
        // Exclude conversations with very few messages (likely onboarding)
        // Onboarding typically has < 5 messages
        if (!c.messages || c.messages.length < 5) {
          console.log(`[Dashboard] Excluding conversation ${c._id} with only ${c.messages?.length || 0} messages`);
          return false;
        }
        
        return true;
      });
      
      console.log('[Dashboard] Filtered conversations:', filteredConversations.map(c => ({
        id: c._id,
        messageCount: c.messages?.length
      })));
      
      // Sort by most recent activity
      const latestConversation = filteredConversations
        .sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt))[0];
      
      console.log('[Dashboard] Selected conversation:', {
        id: latestConversation?._id,
        messageCount: latestConversation?.messages?.length,
        createdAt: latestConversation?.createdAt,
        updatedAt: latestConversation?.updatedAt
      });
      
      // Fetch the actual session summary for the latest conversation
      let sessionSummaryData = null;
      if (latestConversation?._id) {
        try {
          sessionSummaryData = await api.getTherapistSessionSummary(latestConversation._id, token);
          console.log('[Dashboard] Fetched session summary for conversation:', latestConversation._id);
          console.log('[Dashboard] Summary preview:', sessionSummaryData?.therapistSummary?.substring(0, 100));
        } catch (err) {
          console.log('[Dashboard] No session summary available yet:', err.message);
        }
      }

      // Process care notes
      const careNotes = careNotesRes?.careNotes || [];
      const careNotesCount = careNotes.length;

      // Process consultations
      const consultations = consultationsRes?.consultations || [];
      const consultationsCount = consultations.length;

      // Debug: Log onboarding and triage data
      console.log('[ClientDashboard] Onboarding data:', onboardingRes);
      console.log('[ClientDashboard] Triage data:', triageRes);

      setDashboardData({
        checkIns,
        goals,
        journalEntries,
        progressData: progressRes,
        recentActivity,
        stats,
        latestConversation,
        sessionSummaryData,  // Add the fetched session summary data
        careNotesCount,
        consultationsCount,
        onboarding: onboardingRes || { status: 'error' },
        triage: triageRes || { status: 'error' },
        // Risk-summary enrichment (backend: getClientOnboardingDetails). Lifted to
        // the top level for the Risk summary card.
        assignedPersonaName: onboardingRes?.assignedPersonaName ?? null,
        openSignalCount: onboardingRes?.openSignalCount ?? 0
      });

      // Store conversations for AI Supervisor consultation creation
      setAvailableConversations(filteredConversations);

      // Fetch template chart data from the dedicated API endpoint
      try {
        const chartResponse = await getCheckInChartData(clientId, null, 90, token);
        if (chartResponse?.success && chartResponse?.series?.length > 0) {
          setTemplateChartData(chartResponse);
          console.log('[Dashboard] Template chart data loaded:', {
            templateName: chartResponse.template?.name,
            seriesCount: chartResponse.series?.length,
            totalCheckIns: chartResponse.totalCheckIns
          });
        }
      } catch (chartErr) {
        console.log('[Dashboard] Template chart API not available, using legacy format:', chartErr.message);
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const combineRecentActivity = (checkIns, goals, journals) => {
    const activities = [];

    // Add recent check-ins (last 3) - support both legacy and template-based
    checkIns.slice(0, 3).forEach(checkIn => {
      let title, icon;
      const isTemplateBased = checkIn.dynamicResponses?.length > 0;

      if (isTemplateBased) {
        // Template-based check-in
        const templateName = checkIn.templateName || 'Check-in';
        if (checkIn.sudsRating != null) {
          title = `${templateName}: SUDS ${checkIn.sudsRating}/10`;
          icon = checkIn.sudsRating <= 3 ? '😌' : checkIn.sudsRating <= 6 ? '😐' : '😰';
        } else if (checkIn.presenceRating != null) {
          title = `${templateName}: Presence ${checkIn.presenceRating}/10`;
          icon = checkIn.presenceRating >= 7 ? '🧘' : checkIn.presenceRating >= 4 ? '😐' : '🌫️';
        } else {
          title = templateName;
          icon = '📋';
        }
      } else {
        // Legacy check-in
        title = `Mood Check-in: ${checkIn.moodRating}/5`;
        icon = '😊';
      }

      activities.push({
        type: 'checkin',
        title,
        timestamp: checkIn.date || checkIn.createdAt,
        icon
      });
    });

    // Add recent goal updates (last 3)
    goals.slice(0, 3).forEach(goal => {
      if (goal.updatedAt) {
        activities.push({
          type: 'goal',
          title: `Goal: ${goal.description}`,
          timestamp: goal.updatedAt,
          icon: '🎯'
        });
      }
    });

    // Add recent journal entries (last 3)
    journals.slice(0, 3).forEach(entry => {
      activities.push({
        type: 'journal',
        title: entry.title || 'Journal Entry',
        timestamp: entry.createdAt || entry.date,
        icon: '📝'
      });
    });

    // Sort by timestamp
    return activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 10);
  };

  const formatCheckInDataForChart = (checkIns) => {
    // Determine if we have mostly template-based or legacy check-ins
    const templateCheckIns = checkIns.filter(c => c.dynamicResponses?.length > 0 || c.sudsRating != null);
    const legacyCheckIns = checkIns.filter(c => c.moodRating != null);

    if (templateCheckIns.length > legacyCheckIns.length) {
      // Template-based: show SUDS and Presence
      // Filter to only include check-ins with template data, take last 7, and reverse for chronological order
      return checkIns
        .filter(c => c.sudsRating != null || c.presenceRating != null)
        .slice(0, 7)
        .reverse()
        .map(entry => ({
          date: new Date(entry.date || entry.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          suds: entry.sudsRating ?? 0,
          presence: entry.presenceRating ?? 0
        }));
    } else {
      // Legacy: show mood and anxiety
      // Filter to only include check-ins with legacy data, take last 7, and reverse for chronological order
      return checkIns
        .filter(c => c.moodRating != null || c.anxietyRating != null)
        .slice(0, 7)
        .reverse()
        .map(entry => ({
          date: new Date(entry.date || entry.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          mood: entry.moodRating ?? 0,
          anxiety: entry.anxietyRating ?? 0
        }));
    }
  };

  // Determine chart type based on check-in data
  const getChartType = () => {
    // If template chart data is available from API, use that
    if (templateChartData?.series?.length > 0) {
      return 'template-api';
    }
    // Fallback to legacy detection
    const templateCheckIns = dashboardData.checkIns.filter(c => c.dynamicResponses?.length > 0 || c.sudsRating != null);
    const legacyCheckIns = dashboardData.checkIns.filter(c => c.moodRating != null);
    return templateCheckIns.length > legacyCheckIns.length ? 'template' : 'legacy';
  };

  // Transform template API series data to Recharts row format
  const getTemplateChartRows = () => {
    if (!templateChartData?.series?.length) return [];

    // Collect all unique dates across all series
    const dateMap = new Map();

    for (const series of templateChartData.series) {
      for (const point of series.data) {
        const dateStr = new Date(point.x).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        if (!dateMap.has(dateStr)) {
          dateMap.set(dateStr, { date: dateStr, _sortDate: new Date(point.x) });
        }
        // Use series name as the key for this data point
        dateMap.get(dateStr)[series.name] = point.y;
      }
    }

    // Sort by date and return
    return Array.from(dateMap.values())
      .sort((a, b) => a._sortDate - b._sortDate)
      .slice(-7); // Show last 7 data points
  };

  // Get Y-axis domain based on template visualization config
  const getYAxisConfig = () => {
    if (templateChartData?.template?.visualization) {
      const viz = templateChartData.template.visualization;
      return {
        domain: [viz.yAxisMin ?? 0, viz.yAxisMax ?? 10],
        ticks: viz.yAxisMax === 10 ? [0, 2, 4, 6, 8, 10] : [0, 1, 2, 3, 4, 5]
      };
    }
    return getChartType() === 'template'
      ? { domain: [0, 10], ticks: [0, 2, 4, 6, 8, 10] }
      : { domain: [0, 5], ticks: [0, 1, 2, 3, 4, 5] };
  };

  const getMoodTrend = () => {
    if (dashboardData.checkIns.length < 2) return null;
    const recent = dashboardData.checkIns.slice(0, 5);
    const older = dashboardData.checkIns.slice(5, 10);
    const recentAvg = recent.reduce((sum, c) => sum + (c.moodRating || 0), 0) / recent.length;
    const olderAvg = older.length > 0
      ? older.reduce((sum, c) => sum + (c.moodRating || 0), 0) / older.length
      : recentAvg;
    return recentAvg > olderAvg ? 'improving' : recentAvg < olderAvg ? 'declining' : 'stable';
  };

  // AI Supervisor menu handlers
  const handleAiSupervisorMenuOpen = (event) => {
    setAiSupervisorMenuAnchor(event.currentTarget);
  };

  const handleAiSupervisorMenuClose = () => {
    setAiSupervisorMenuAnchor(null);
  };

  const handleViewConsultations = () => {
    handleAiSupervisorMenuClose();
    navigate(`/therapist/dashboard?tab=consultations&clientId=${clientId}`, {
      state: {
        returnTo: 'clientDashboard',
        clientId,
        clientName: clientInfo?.name,
        client: clientInfo // Pass the full client object for navigation back
      }
    });
  };

  const handleStartNewConsultation = () => {
    handleAiSupervisorMenuClose();
    if (availableConversations.length === 0) {
      setSnackbarMessage('No conversations available. Please have at least one therapy conversation with this client first.');
      return;
    }
    if (availableConversations.length === 1) {
      // Only one conversation, navigate directly
      navigate(`/supervisor/consultation/new/${clientId}/${availableConversations[0]._id}`);
    } else {
      // Multiple conversations, show dialog
      setConversationDialogOpen(true);
    }
  };

  const handleConversationSelect = (conversationId) => {
    setConversationDialogOpen(false);
    navigate(`/supervisor/consultation/new/${clientId}/${conversationId}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  const moodTrend = getMoodTrend();
  const chartType = getChartType();
  const yAxisConfig = getYAxisConfig();

  // Use template API data if available, otherwise fall back to legacy format
  const chartData = chartType === 'template-api'
    ? getTemplateChartRows()
    : formatCheckInDataForChart(dashboardData.checkIns);

  // Debug logging for chart data
  console.log('[Dashboard] Check-ins:', dashboardData.checkIns?.length, '| Chart type:', chartType, '| Data points:', chartData.length);
  if (chartData.length > 0) {
    console.log('[Dashboard] Sample chart data:', chartData[0]);
  }
  if (templateChartData) {
    console.log('[Dashboard] Template series:', templateChartData.series?.map(s => s.name));
  }

  return (
    <Box sx={{ p: 2 }}>

      {/* Client snapshot — Risk / Onboarding / Upcoming as a single 3-card row
          (audit Row 1). All three cards use the same dashboard data. */}
      <Grid container spacing={2} sx={{ mb: 3 }} alignItems="stretch">
          <Grid size={{ xs: 12, md: 4 }}>
            {/* Risk summary card (audit Row 1) — triage risk + assigned persona +
                open signal count, using the enriched dashboard data. */}
            <Paper sx={{ ...therapistStyles.mainCard, p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={therapistStyles.sectionHeader}>Risk summary</Typography>
                <Button size="small" onClick={() => onNavigateToTab && onNavigateToTab(7)} sx={therapistStyles.outlinedButton}>
                  Assessments
                </Button>
              </Box>
              {(() => {
                const risk = dashboardData.triage?.riskLevel;
                const tier = risk === 'high' ? 'high' : risk === 'medium' ? 'moderate' : risk === 'low' ? 'routine' : null;
                const c = tier ? statusColor(tier) : null;
                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Risk level</Typography>
                      {tier ? (
                        <Chip label={risk.charAt(0).toUpperCase() + risk.slice(1)} size="small"
                          sx={{ backgroundColor: c.bg, color: c.fg, fontWeight: 600 }} />
                      ) : (
                        <Chip label="Not assessed" size="small" />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">Assigned persona</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>
                        {dashboardData.assignedPersonaName || '—'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Open signals</Typography>
                      <Chip label={dashboardData.openSignalCount || 0} size="small"
                        color={dashboardData.openSignalCount > 0 ? 'error' : 'default'} />
                    </Box>
                  </Box>
                );
              })()}
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <OnboardingStatusCard
              onboarding={dashboardData.onboarding}
              loading={loading}
              onViewAssessments={() => onNavigateToTab && onNavigateToTab(7)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            {apiContext === 'therapist' && clientId ? (
              <UpcomingAppointmentsWidget
                token={token}
                role="therapist"
                clientIdFilter={clientId}
                title="Upcoming calls"
              />
            ) : null}
          </Grid>
        </Grid>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: dashboardData.consultationsCount > 0 ? 2.4 : 3 }}>
          <Card sx={therapistStyles.statsCard}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography color="text.secondary" variant="caption">
                    Average {dashboardData.stats.averageLabel || 'Mood'}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: tokens.color.accent[500] }}>
                    {dashboardData.stats.averageMood}
                  </Typography>
                  {moodTrend && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      {moodTrend === 'improving' ? (
                        <TrendingUpIcon sx={{ fontSize: 16, color: tokens.color.status.routine.solid, mr: 0.5 }} />
                      ) : moodTrend === 'declining' ? (
                        <TrendingDownIcon sx={{ fontSize: 16, color: tokens.color.status.critical.solid, mr: 0.5 }} />
                      ) : null}
                      <Typography variant="caption" sx={{
                        color: moodTrend === 'improving' ? tokens.color.status.routine.solid :
                               moodTrend === 'declining' ? tokens.color.status.critical.solid : 'text.secondary'
                      }}>
                        {moodTrend}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: dashboardData.consultationsCount > 0 ? 2.4 : 3 }}>
          <Card sx={therapistStyles.statsCard}>
            <CardContent>
              <Typography color="text.secondary" variant="caption">
                Check-ins
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                {dashboardData.stats.totalCheckIns}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total recorded
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: dashboardData.consultationsCount > 0 ? 2.4 : 3 }}>
          <Card sx={therapistStyles.statsCard}>
            <CardContent>
              <Typography color="text.secondary" variant="caption">
                Active Goals
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: tokens.color.status.info.solid }}>
                {dashboardData.stats.activeGoals}
              </Typography>
              <Typography variant="caption" color="success.main">
                {dashboardData.stats.completedGoals} completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: dashboardData.consultationsCount > 0 ? 2.4 : 3 }}>
          <Card sx={therapistStyles.statsCard}>
            <CardContent>
              <Typography color="text.secondary" variant="caption">
                Journal Entries
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                {dashboardData.stats.totalJournalEntries}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total entries
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Consultations Card - Only show if consultations exist */}
        {dashboardData.consultationsCount > 0 && (
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card 
              sx={{
                ...therapistStyles.statsCard,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: dashboardTheme.shadows.large
                }
              }}
              onClick={() => {
                // Navigate to consultations with client filter
                navigate(`/therapist/dashboard?tab=consultations&clientId=${clientId}`);
              }}
            >
              <CardContent>
                <Typography color="text.secondary" variant="caption">
                  Consultations
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#6366F1' }}>
                  {dashboardData.consultationsCount}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: '#6366F1',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  View All →
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <Grid container spacing={3}>
        {/* Check-in Trends Chart - adapts to legacy or template-based */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper sx={{ ...therapistStyles.mainCard, p: 3 }}>
            <Typography variant="h6" sx={therapistStyles.sectionHeader}>
              {chartType === 'template-api' && templateChartData?.template?.name
                ? `${templateChartData.template.name} Trends`
                : chartType === 'template' ? 'SUDS & Presence Trends' : 'Mood Trends'}
            </Typography>
            {chartData.length > 0 ? (
              <Box sx={{ mt: 2, width: '100%', height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" />
                  <YAxis domain={yAxisConfig.domain} ticks={yAxisConfig.ticks} />
                  <RechartsTooltip />
                  <Legend />
                  {chartType === 'template-api' && templateChartData?.series
                    ? templateChartData.series.map((series, idx) => (
                        <Line
                          key={series.name}
                          type="monotone"
                          dataKey={series.name}
                          stroke={series.color || tokens.chartSeries[idx % tokens.chartSeries.length]}
                          strokeWidth={2}
                          dot={{ fill: series.color || tokens.chartSeries[idx % tokens.chartSeries.length], r: 4 }}
                          name={series.name}
                          connectNulls
                        />
                      ))
                    : chartType === 'template'
                    ? [
                        <Line
                          key="suds"
                          type="monotone"
                          dataKey="suds"
                          stroke={tokens.color.status.critical.solid}
                          strokeWidth={2}
                          dot={{ fill: tokens.color.status.critical.solid, r: 4 }}
                          name="SUDS (Distress)"
                          connectNulls
                        />,
                        <Line
                          key="presence"
                          type="monotone"
                          dataKey="presence"
                          stroke={tokens.color.status.routine.solid}
                          strokeWidth={2}
                          dot={{ fill: tokens.color.status.routine.solid, r: 4 }}
                          name="Presence"
                          connectNulls
                        />
                      ]
                    : [
                        <Line
                          key="mood"
                          type="monotone"
                          dataKey="mood"
                          stroke={tokens.color.accent[500]}
                          strokeWidth={2}
                          dot={{ fill: tokens.color.accent[500], r: 4 }}
                          name="Mood"
                          connectNulls
                        />,
                        <Line
                          key="anxiety"
                          type="monotone"
                          dataKey="anxiety"
                          stroke={tokens.color.status.info.solid}
                          strokeWidth={2}
                          dot={{ fill: tokens.color.status.info.solid, r: 4 }}
                          name="Anxiety"
                          connectNulls
                        />
                      ]
                  }
                </LineChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No check-in data available
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Quick Actions */}
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <Paper sx={{ ...therapistStyles.mainCard, p: 3 }}>
            <Typography variant="h6" sx={therapistStyles.sectionHeader}>
              Quick Actions
            </Typography>
            {/* Ticket F2 — 2-column compact tile grid (was a 7-row list). Halves the
                card height so it bottom-aligns better with the Mood Trends chart and
                closes the left-column void. */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
              {[
                { label: 'Conversations', icon: <ChatIcon sx={{ color: '#FF8C00' }} />, onClick: () => onNavigateToTab(4) },
                { label: 'Goals', icon: <TrackChangesIcon sx={{ color: tokens.color.status.info.solid }} />, onClick: () => onNavigateToTab(1) },
                { label: 'Assessments', icon: <AssignmentIcon sx={{ color: '#9C27B0' }} />, onClick: () => onNavigateToTab(7) },
                { label: 'Session Summary', icon: <PsychologyIcon sx={{ color: '#607D8B' }} />, onClick: () => onNavigateToTab(10) },
                { label: 'Client Alerts', icon: <NotificationsActiveIcon sx={{ color: '#F44336' }} />, onClick: () => onNavigateToTab(9) },
                { label: 'AI Supervisor', icon: <SupervisorAccountIcon sx={{ color: '#6366F1' }} />, onClick: handleAiSupervisorMenuOpen },
                ...(dashboardData.careNotesCount > 0
                  ? [{ label: 'Care Notes', icon: <EventNoteIcon sx={{ color: '#4CAF50' }} />, onClick: () => onNavigateToTab(8),
                       badge: `${dashboardData.careNotesCount} note${dashboardData.careNotesCount > 1 ? 's' : ''}` }]
                  : []),
              ].map((a) => (
                <Button
                  key={a.label}
                  onClick={a.onClick}
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textTransform: 'none',
                    gap: 0.5,
                    p: 1.5,
                    minHeight: 84,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    color: 'text.primary',
                  }}
                >
                  {a.icon}
                  <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'center', lineHeight: 1.2 }}>
                    {a.label}
                  </Typography>
                  {a.badge && (
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                      {a.badge}
                    </Typography>
                  )}
                </Button>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Active Goals — first of the 3-equal-card row (F1) */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Paper sx={{ ...therapistStyles.mainCard, p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={therapistStyles.sectionHeader}>
                Active Goals
              </Typography>
              <Button
                size="small"
                onClick={() => onNavigateToTab(1)}
                sx={therapistStyles.outlinedButton}
              >
                View All
              </Button>
            </Box>
            {dashboardData.goals.filter(g => g.status === 'active').slice(0, 3).length > 0 ? (
              <List>
                {dashboardData.goals.filter(g => g.status === 'active').slice(0, 3).map((goal, index) => (
                  <React.Fragment key={goal._id}>
                    {index > 0 && <Divider />}
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        {goal.status === 'completed' ? (
                          <CheckCircleIcon sx={{ color: '#66BB6A' }} />
                        ) : (
                          <RadioButtonUncheckedIcon sx={{ color: '#FF8C00' }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={goal.description}
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Chip
                              label={goal.goalType === 'one_off' ? 'Task' : 'Habit'}
                              size="small"
                              sx={{ height: 20, fontSize: '0.75rem' }}
                            />
                            {goal.progress && (
                              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={goal.progress}
                                  sx={{ flex: 1, height: 4 }}
                                />
                                <Typography variant="caption">{goal.progress}%</Typography>
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No active goals
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Recent Activity — second of the 3-equal-card row (F1) */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Paper sx={{ ...therapistStyles.mainCard, p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={therapistStyles.sectionHeader}>
                Recent Activity
              </Typography>
              <Button 
                size="small" 
                onClick={() => onNavigateToTab(6)}
                sx={therapistStyles.outlinedButton}
              >
                View All
              </Button>
            </Box>
            {dashboardData.recentActivity.length > 0 ? (
              <List>
                {dashboardData.recentActivity.slice(0, 5).map((activity, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <Divider />}
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Typography variant="h6">{activity.icon}</Typography>
                      </ListItemIcon>
                      <ListItemText 
                        primary={activity.title}
                        secondary={formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No recent activity
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Session Insights — third of the 3-equal-card row (F1) */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Paper sx={{ ...therapistStyles.mainCard, p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={therapistStyles.sectionHeader}>
                Session Insights & Reflections
              </Typography>
              {dashboardData.latestConversation && (
                <Chip 
                  label={dashboardData.latestConversation.controlMode === 'ai' ? 'AI Session' : 'Human Session'}
                  size="small"
                  sx={{ 
                    backgroundColor: dashboardData.latestConversation.controlMode === 'ai' ? '#E3F2FD' : '#FFF3E0',
                    color: dashboardData.latestConversation.controlMode === 'ai' ? '#1976D2' : '#F57C00'
                  }}
                />
              )}
            </Box>
            {dashboardData.sessionSummaryData?.clientSummary || dashboardData.sessionSummaryData?.therapistSummary ? (
              <Box>
                {/* Therapist Summary */}
                {dashboardData.sessionSummaryData?.therapistSummary && (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#FF8C00' }}>
                      Therapist Summary:
                    </Typography>
                    <Paper elevation={0} sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ 
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.6,
                        color: '#424242',
                        maxHeight: '150px',
                        overflow: 'auto'
                      }}>
                        {dashboardData.sessionSummaryData.therapistSummary.substring(0, 300)}
                        {dashboardData.sessionSummaryData.therapistSummary.length > 300 ? '...' : ''}
                      </Typography>
                    </Paper>
                  </>
                )}
                
                {/* Client Summary */}
                {dashboardData.sessionSummaryData?.clientSummary && (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#42A5F5' }}>
                      Client Summary:
                    </Typography>
                    <Paper elevation={0} sx={{ p: 1.5, mb: 2, bgcolor: 'blue.50', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ 
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.6,
                        color: '#424242',
                        maxHeight: '150px',
                        overflow: 'auto'
                      }}>
                        {dashboardData.sessionSummaryData.clientSummary.substring(0, 300)}
                        {dashboardData.sessionSummaryData.clientSummary.length > 300 ? '...' : ''}
                      </Typography>
                    </Paper>
                  </>
                )}
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Generated {new Date(dashboardData.latestConversation.updatedAt || dashboardData.latestConversation.createdAt).toLocaleDateString()}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => onNavigateToTab(10)}
                    sx={{ ...therapistStyles.outlinedButton, py: 0.5, fontSize: '0.75rem' }}
                  >
                    View Full Summary
                  </Button>
                </Box>
              </Box>
            ) : dashboardData.latestConversation ? (
              <Box>
                <Alert severity="info" sx={{ backgroundColor: 'rgba(66, 165, 245, 0.08)' }}>
                  <Typography variant="body2">
                    Session insights will be generated after the conversation ends. The AI analyzes the discussion to provide:
                  </Typography>
                  <List dense sx={{ mt: 1 }}>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText primary="• Key themes and topics discussed" />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText primary="• Client's emotional state and progress" />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText primary="• Recommendations for follow-up" />
                    </ListItem>
                  </List>
                </Alert>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No sessions available yet. Insights will appear here after client sessions.
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Latest Conversation Messages - Full width below */}
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ ...therapistStyles.mainCard, p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={therapistStyles.sectionHeader}>
                Latest Conversation
              </Typography>
              <Button 
                size="small" 
                onClick={() => onNavigateToTab(4)}
                sx={therapistStyles.outlinedButton}
              >
                View All Sessions
              </Button>
            </Box>
            {dashboardData.latestConversation ? (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {new Date(dashboardData.latestConversation.updatedAt || dashboardData.latestConversation.createdAt).toLocaleDateString('en-US', { 
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                  })}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Session Mode:</strong> {dashboardData.latestConversation.controlMode === 'ai' ? 'AI Therapist' : 'Human Therapist'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Messages:</strong> {dashboardData.latestConversation.messages?.length || 0}
                </Typography>
                {dashboardData.latestConversation.sessionSummary ? (
                  <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(0,0,0,0.02)', borderRadius: '8px' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {dashboardData.latestConversation.sessionSummary}
                    </Typography>
                  </Box>
                ) : dashboardData.latestConversation.messages && dashboardData.latestConversation.messages.length > 0 ? (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Recent Messages:
                    </Typography>
                    {dashboardData.latestConversation.messages.slice(-3).map((msg, idx) => (
                      <Box key={idx} sx={{ mb: 1, p: 1, backgroundColor: 'rgba(0,0,0,0.02)', borderRadius: '8px' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#FF8C00' }}>
                          {msg.senderRole === 'client' ? 'Client' : msg.senderRole === 'ai' ? 'AI' : 'Therapist'}:
                        </Typography>
                        <Tooltip title={msg.text} placement="top" arrow>
                          <Typography variant="body2" sx={{ mt: 0.5, cursor: 'help' }}>
                            {msg.text?.substring(0, 150)}{msg.text?.length > 150 ? '...' : ''}
                          </Typography>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No session summary available yet.
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No sessions recorded yet.
              </Typography>
            )}
          </Paper>
        </Grid>

      </Grid>

      {/* AI Supervisor Menu */}
      <Menu
        anchorEl={aiSupervisorMenuAnchor}
        open={Boolean(aiSupervisorMenuAnchor)}
        onClose={handleAiSupervisorMenuClose}
      >
        <MenuItem onClick={handleViewConsultations}>
          <ListItemIcon>
            <MenuBookIcon fontSize="small" sx={{ color: '#6366F1' }} />
          </ListItemIcon>
          <ListItemText primary="View Consultations" secondary={dashboardData.consultationsCount > 0 ? `${dashboardData.consultationsCount} consultation${dashboardData.consultationsCount > 1 ? 's' : ''}` : 'None yet'} />
        </MenuItem>
        <MenuItem onClick={handleStartNewConsultation}>
          <ListItemIcon>
            <ChatIcon fontSize="small" sx={{ color: '#6366F1' }} />
          </ListItemIcon>
          <ListItemText primary="Start New Consultation" secondary="Get clinical guidance" />
        </MenuItem>
      </Menu>

      {/* Conversation Selection Dialog */}
      <Dialog
        open={conversationDialogOpen}
        onClose={() => setConversationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Conversation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which conversation you'd like clinical guidance on:
          </Typography>
          <List>
            {availableConversations.map((conversation, index) => (
              <React.Fragment key={conversation._id}>
                {index > 0 && <Divider />}
                <ListItem
                  button
                  onClick={() => handleConversationSelect(conversation._id)}
                  sx={{ py: 2 }}
                >
                  <ListItemIcon>
                    <ChatIcon sx={{ color: '#6366F1' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={`Conversation ${availableConversations.length - index}`}
                    secondary={`${conversation.messages?.length || 0} messages • ${formatDistanceToNow(new Date(conversation.updatedAt || conversation.createdAt), { addSuffix: true })}`}
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConversationDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={() => setSnackbarMessage('')} variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientDashboardView;